import DOMPurify from "dompurify";
import type {ClipboardEvent} from "react";

const COPY_CONTROLS = '.copy-table-overlay, [data-copy-control], [role="tooltip"], [data-tooltip], [hidden], [aria-hidden="true"]';
// HTML parsing removes null characters, so this separator cannot collide with response text.
const CODE_BLOCK_SEPARATOR = "\u0000";

/** Keep document structure, without carrying the chat's theme into an email or document. */
export function getResponseClipboardContent(source: Node): {html: string; text: string} {
   const container = document.createElement("div");
   container.append(source.cloneNode(true));
   container.querySelectorAll(COPY_CONTROLS).forEach(element => element.remove());
   container.innerHTML = DOMPurify.sanitize(container.innerHTML, {
      ALLOWED_TAGS: ["div", "span", "p", "br", "hr", "h1", "h2", "h3", "h4", "h5", "h6",
         "strong", "b", "em", "i", "u", "s", "del", "a", "blockquote", "pre", "code",
         "ul", "ol", "li", "table", "caption", "thead", "tbody", "tfoot", "tr", "th", "td", "sub", "sup"],
      ALLOWED_ATTR: ["href", "title", "colspan", "rowspan", "start", "reversed", "value"],
      ALLOW_DATA_ATTR: false,
      ALLOW_ARIA_ATTR: false,
   });
   const codeBlocks: string[] = [];
   const text = getPlainText(container, codeBlocks).trim()
      .split(CODE_BLOCK_SEPARATOR)
      .map((part, index) => index % 2 === 0 ? part : codeBlocks[Number(part)])
      .join("")
      .replaceAll(/\r?\n/g, "\r\n");
   return {html: container.innerHTML, text};
}

// Detached innerText loses block boundaries. Serialize semantic blocks explicitly,
// including list markers and TSV tables, without relying on the page's CSS.
function getPlainText(node: Node, codeBlocks: string[]): string {
   if (node.nodeType === Node.TEXT_NODE) return (node.textContent ?? "").replaceAll(/\s+/g, " ");
   if (!(node instanceof HTMLElement)) return "";
   if (node.tagName === "BR") return "\n";
   // Protect code whitespace from paragraph normalization. Splitting the paired
   // separators later puts code-block indices in the odd-numbered segments.
   if (node.tagName === "PRE") return `\n\n${CODE_BLOCK_SEPARATOR}${codeBlocks.push(node.textContent ?? "") - 1}${CODE_BLOCK_SEPARATOR}\n\n`;
   if (node instanceof HTMLTableElement) {
      const rows = Array.from(node.rows, row => Array.from(row.cells, cell =>
         getPlainText(cell, codeBlocks).replaceAll(/\s+/g, " ").trim()).join("\t"));
      return `\n\n${rows.join("\n")}\n\n`;
   }
   if (node.tagName === "UL" || node.tagName === "OL") {
      const items = Array.from(node.children).filter(child => child.tagName === "LI");
      const reversed = node.hasAttribute("reversed");
      let number = Number(node.getAttribute("start") ?? (reversed ? items.length : 1));
      return "\n\n" + items.map(item => {
         if (item.hasAttribute("value")) number = Number(item.getAttribute("value"));
         const marker = node.tagName === "OL" ? `${number}. ` : "- ";
         number += reversed ? -1 : 1;
         return marker + getPlainText(item, codeBlocks).trim().replaceAll("\n", "\n  ");
      }).join("\n") + "\n\n";
   }
   // Ignore whitespace-only separators between blocks, but retain spaces between inline elements.
   const text = Array.from(node.childNodes, child => getPlainText(child, codeBlocks)).join("")
      .replaceAll(/\n +(?=\n)/g, "\n").replaceAll(/\n{3,}/g, "\n\n");
   return /^(P|H[1-6]|BLOCKQUOTE|DIV|HR)$/.test(node.tagName) ? `\n\n${text.trim()}\n\n` : text;
}

/** Handle native keyboard/context-menu copy only when the selection belongs to this response. */
export function copyResponseSelection(event: ClipboardEvent<HTMLDivElement>): void {
   if (event.defaultPrevented || (event.target instanceof HTMLElement &&
      (event.target.isContentEditable || event.target.closest("input, textarea")))) return;
   const root = event.currentTarget;
   const selection = root.ownerDocument.getSelection();
   if (!selection || selection.isCollapsed || selection.rangeCount !== 1) return;
   const range = selection.getRangeAt(0);
   if (!root.contains(range.startContainer) || !root.contains(range.endContainer)) return;

   let content: Node = range.cloneContents();
   let ancestor = range.commonAncestorContainer;
   if (ancestor.nodeType === Node.TEXT_NODE) ancestor = ancestor.parentNode!;
   // Preserve enclosing links, emphasis, and table/list structure for partial selections.
   while (ancestor !== root) {
      const wrapper = ancestor.cloneNode(false);
      wrapper.appendChild(content);
      content = wrapper;
      ancestor = ancestor.parentNode!;
   }
   const {html, text} = getResponseClipboardContent(content);
   // A substring of a text node should keep exactly the selected spaces and
   // characters, without adding enclosing list markers or paragraph breaks.
   const plain = range.startContainer === range.endContainer && range.startContainer.nodeType === Node.TEXT_NODE
      ? range.toString()
      : text;
   event.clipboardData.setData("text/html", html);
   event.clipboardData.setData("text/plain", plain);
   event.preventDefault();
}
